// AuthMfaFlow (contract 06 §8). Validates the code as an exact 6-digit STRING WITHOUT numeric
// coercion ("012345" preserved), verifies against the memory-only temp token, and replaces the
// whole challenge on resend. Attempts/expiry/lockout/cooldown are server-authored — never
// invented locally. Post-signup Shopify completion (module 12) is deferred to a later wave.
import { AuthError } from '../models/auth.error'
import type { MfaChallenge } from '../models/auth.types'
import { authService, type AuthService } from '../service/AuthService'
import { useAuthStore, type AuthStore } from '../stores/authStore'

import { isForeignFrontendOrigin, redirectToMcpShellLogin } from './mcpShellRedirect'

const CODE_RE = /^\d{6}$/

export interface AuthMfaDeps {
  service?: AuthService
  store?: AuthStore
}

export class AuthMfaFlow {
  private readonly service: AuthService
  private readonly store: AuthStore

  constructor(deps: AuthMfaDeps = {}) {
    this.service = deps.service ?? authService
    this.store = deps.store ?? useAuthStore()
  }

  /** Verify the 6-digit code against the memory-only temp token; on success set the bearer. */
  async verifyCode(code: string): Promise<void> {
    if (!CODE_RE.test(code)) throw new AuthError('mfaInvalidCode', 'auth.mfa.invalidCode')
    const tempToken = this.store.tempToken
    if (!tempToken) throw new AuthError('mfaExpired', 'auth.mfa.expired') // refresh restarted login
    try {
      const serviceWithResult = this.service as AuthService & {
        verifyMfaResult?: AuthService['verifyMfaResult']
      }
      const result = serviceWithResult.verifyMfaResult
        ? await serviceWithResult.verifyMfaResult({ temp_token: tempToken, code })
        : { token: await this.service.verifyMfa({ temp_token: tempToken, code }) }
      if (
        typeof result.frontend_origin === 'string' &&
        isForeignFrontendOrigin(result.frontend_origin) &&
        result.handoff
      ) {
        redirectToMcpShellLogin(result.frontend_origin, result.handoff)
        return
      }
      this.store.setToken(result.token)
      this.store.setTempToken(null) // consumed
      this.store.setMfaChallenge(null) // the challenge died with the token it belonged to
    } catch (error) {
      throw AuthError.from(error)
    }
  }

  /** Request a fresh code; the server response REPLACES the whole challenge (+ temp token). */
  async resendCode(): Promise<MfaChallenge> {
    const tempToken = this.store.tempToken
    if (!tempToken) throw new AuthError('mfaExpired', 'auth.mfa.expired')
    try {
      const resent = await this.service.resendMfa({ temp_token: tempToken })
      // A resend does not repeat `mfa_methods`, but the replacement challenge still takes the
      // authenticator code — keep the hint the sign-in announced instead of silently dropping it.
      const methods = this.store.mfaChallenge?.mfa_methods
      const challenge: MfaChallenge = methods ? { ...resent, mfa_methods: methods } : resent
      this.store.startMfaChallenge(challenge)
      return challenge
    } catch (error) {
      throw AuthError.from(error)
    }
  }
}
